
import { supabase } from "@/integrations/supabase/client";

interface RetryConfig {
  maxRetries: number;
  backoffMultiplier: number;
  initialDelay: number;
  maxDelay: number;
}

interface CircuitBreakerConfig {
  failureThreshold: number;
  resetTimeout: number;
  halfOpenTimeout: number;
}

export class ErrorRecoveryManager {
  private retryConfig: RetryConfig;
  private circuitBreakerConfig: CircuitBreakerConfig;
  private failureCount: number = 0;
  private lastFailureTime: number = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private static WORKFLOW_CONTROL_ID = '67e2e098-d495-4c63-a656-4ebb2d46e2df';

  constructor(agentId: string = ErrorRecoveryManager.WORKFLOW_CONTROL_ID) {
    this.retryConfig = {
      maxRetries: 3,
      backoffMultiplier: 2,
      initialDelay: 1000,
      maxDelay: 30000
    };
    this.circuitBreakerConfig = {
      failureThreshold: 5,
      resetTimeout: 300000, // 5 minutes
      halfOpenTimeout: 30000 // 30 seconds
    };
    this.loadConfig(agentId);
  }

  private async loadConfig(agentId: string) {
    try {
      const { data: agent } = await supabase
        .from('agent_types')
        .select('retry_strategy, circuit_breaker_config')
        .eq('id', agentId)
        .single();

      if (agent) {
        if (typeof agent.retry_strategy === 'object' && agent.retry_strategy) {
          const retryStrategyData = agent.retry_strategy as Record<string, unknown>;
          if ('maxRetries' in retryStrategyData && 
              'backoffMultiplier' in retryStrategyData && 
              'initialDelay' in retryStrategyData && 
              'maxDelay' in retryStrategyData) {
            this.retryConfig = {
              maxRetries: Number(retryStrategyData.maxRetries),
              backoffMultiplier: Number(retryStrategyData.backoffMultiplier),
              initialDelay: Number(retryStrategyData.initialDelay),
              maxDelay: Number(retryStrategyData.maxDelay)
            };
          }
        }
        
        if (typeof agent.circuit_breaker_config === 'object' && agent.circuit_breaker_config) {
          const circuitBreakerData = agent.circuit_breaker_config as Record<string, unknown>;
          if ('failureThreshold' in circuitBreakerData && 
              'resetTimeout' in circuitBreakerData && 
              'halfOpenTimeout' in circuitBreakerData) {
            this.circuitBreakerConfig = {
              failureThreshold: Number(circuitBreakerData.failureThreshold),
              resetTimeout: Number(circuitBreakerData.resetTimeout),
              halfOpenTimeout: Number(circuitBreakerData.halfOpenTimeout)
            };
          }
        }
      }
    } catch (error) {
      console.error('Error loading agent configuration:', error);
    }
  }

  async executeWithRecovery<T>(
    operation: () => Promise<T>,
    context: { agentId: string; operationType: string }
  ): Promise<T> {
    if (this.isCircuitOpen()) {
      throw new Error('Circuit breaker is open');
    }

    let attempts = 0;
    let lastError: Error;

    while (attempts < this.retryConfig.maxRetries) {
      try {
        const result = await operation();
        this.onSuccess();
        return result;
      } catch (error) {
        lastError = error as Error;
        attempts++;
        
        await this.logError(context, lastError, attempts);
        
        if (attempts < this.retryConfig.maxRetries) {
          const delay = this.calculateBackoff(attempts);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    this.onFailure();
    throw new Error(`Operation failed after ${attempts} attempts. Last error: ${lastError?.message}`);
  }

  private calculateBackoff(attempt: number): number {
    const delay = this.retryConfig.initialDelay * 
      Math.pow(this.retryConfig.backoffMultiplier, attempt - 1);
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  private isCircuitOpen(): boolean {
    if (this.state === 'OPEN') {
      const now = Date.now();
      if (now - this.lastFailureTime >= this.circuitBreakerConfig.resetTimeout) {
        this.state = 'HALF_OPEN';
        return false;
      }
      return true;
    }
    return false;
  }

  private onSuccess(): void {
    if (this.state === 'HALF_OPEN') {
      this.state = 'CLOSED';
      this.failureCount = 0;
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.circuitBreakerConfig.failureThreshold) {
      this.state = 'OPEN';
    }
  }

  private async logError(
    context: { agentId: string; operationType: string },
    error: Error,
    attempt: number
  ): Promise<void> {
    try {
      await supabase.from('error_tracking').insert({
        error_type: context.operationType,
        severity: 'error',
        message: error.message,
        stack_trace: error.stack,
        context: {
          agent_id: context.agentId,
          attempt,
          circuit_breaker_state: this.state,
          failure_count: this.failureCount
        }
      });
    } catch (loggingError) {
      console.error('Error logging failure:', loggingError);
    }
  }
}
